/**
 * Authentication Service
 * Now uses backend API instead of direct Supabase connection
 */

import { User, UserRole } from '../types';
import { getApiBaseUrl } from '../config/api';

const API_BASE_URL = getApiBaseUrl();
const VALID_ROLES: UserRole[] = ['admin', 'owner', 'manager', 'employee'];

function parseJwtPayload(token: string): any | null {
  try {
    return JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return null;
  }
}

function normalizeUser(user: User | null | undefined, token?: string | null): User | null {
  if (!user) return null;

  const payload = token ? parseJwtPayload(token) : null;
  const tokenRole = payload?.user_metadata?.role || payload?.app_metadata?.role;
  const role = VALID_ROLES.includes(user.role) ? user.role : (VALID_ROLES.includes(tokenRole) ? tokenRole : 'employee');
  const brandId = user.brandId || payload?.user_metadata?.brandId || payload?.app_metadata?.brandId;
  const stageBrandId = user.stageBrandId || payload?.user_metadata?.stageBrandId || payload?.app_metadata?.stageBrandId;

  return {
    ...user,
    role,
    brandId,
    stageBrandId,
  };
}

export interface BrandSignupPayload {
  email: string;
  password: string;
  name: string;
  brandName: string;
  legalName: string;
  taxNumber: string;
  taxOffice: string;
  companyType: 'sole_proprietorship' | 'limited' | 'joint_stock' | 'other';
  websiteUrl?: string;
  phone?: string;
  billingEmail?: string;
  billingAddress: {
    line1: string;
    line2?: string;
    city: string;
    district?: string;
    postalCode?: string;
    country: string;
  };
}

export class AuthService {
  /**
   * Sign in with email and password
   */
  static async signIn(email: string, password: string): Promise<User> {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Sign in failed');
      }

      console.log('🔐 AuthService - Login successful, received token:', data.token ? `${data.token.substring(0, 20)}...` : 'NO TOKEN');

      // Store tokens in localStorage
      localStorage.setItem('token', data.token);
      localStorage.setItem('refreshToken', data.refreshToken);

      console.log('💾 AuthService - Token stored in localStorage');

      return normalizeUser(data.user, data.token)!;
    } catch (error: any) {
      throw new Error(error.message || 'Sign in failed');
    }
  }

  /**
   * Sign up new user
   */
  static async signUp(
    email: string,
    password: string,
    name: string,
    role: UserRole = 'employee'
  ): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password, name, role }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Sign up failed');
      }
    } catch (error: any) {
      throw new Error(error.message || 'Sign up failed');
    }
  }

  /**
   * Sign out current user
   */
  static async signOut(): Promise<void> {
    try {
      const token = localStorage.getItem('token');
      
      if (token) {
        await fetch(`${API_BASE_URL}/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
      }

      // Clear tokens from localStorage
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
    } catch (error: any) {
      // Always clear tokens even if API call fails
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      throw new Error(error.message || 'Sign out failed');
    }
  }

  /**
   * Get current authenticated user
   */
  static async getCurrentUser(): Promise<User | null> {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        return null;
      }

      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        // Token might be expired, try to refresh
        const refreshed = await this.refreshSession();
        if (refreshed) {
          // Retry getting user
          return await this.getCurrentUser();
        }
        return null;
      }

      return normalizeUser(data.user, token);
    } catch (error) {
      console.error('Error getting current user:', error);
      return null;
    }
  }

  /**
   * Check if user is authenticated
   */
  static async isAuthenticated(): Promise<boolean> {
    try {
      const user = await AuthService.getCurrentUser();
      return user !== null;
    } catch {
      return false;
    }
  }

  /**
   * Get user session token
   */
  static async getToken(): Promise<string | null> {
    return localStorage.getItem('token');
  }

  /**
   * Forgot password - send reset email
   */
  static async forgotPassword(email: string): Promise<void> {
    // TODO: Implement forgot password endpoint in backend
    throw new Error('Forgot password not implemented yet');
  }

  /**
   * Confirm password reset with new password
   */
  static async confirmPassword(newPassword: string): Promise<void> {
    // TODO: Implement password reset endpoint in backend
    throw new Error('Password reset not implemented yet');
  }

  /**
   * Refresh session (get new token)
   */
  static async refreshSession(): Promise<boolean> {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      
      if (!refreshToken) {
        return false;
      }

      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        return false;
      }

      // Update tokens
      localStorage.setItem('token', data.token);
      localStorage.setItem('refreshToken', data.refreshToken);

      return true;
    } catch (error) {
      console.error('Error refreshing session:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      return false;
    }
  }

  /**
   * Update user attributes (name, etc.)
   */
  static async updateUserAttributes(attributes: { name?: string }): Promise<void> {
    // TODO: Implement update user endpoint in backend
    throw new Error('Update user attributes not implemented yet');
  }

  /**
   * Set brand IDs for current user
   */
  static async setBrandIds(brandId?: string, stageBrandId?: string): Promise<void> {
    const token = await this.getToken();
    if (!token) {
      throw new Error('No user logged in');
    }

    const response = await fetch(`${API_BASE_URL}/auth/update-brand-ids`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ brandId, stageBrandId }),
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.error || 'Failed to update brand IDs');
    }
  }

  /**
   * Start email-verified commercial brand signup.
   * Does not store tokens; account activates after email verification.
   */
  static async registerBrand(payload: BrandSignupPayload): Promise<{ pendingBrand: { id: string; name: string; isActive: boolean }; message: string }> {
    const response = await fetch(`${API_BASE_URL}/auth/register-brand`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    const data = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(data.error || 'Marka kaydı başlatılamadı');
    }

    localStorage.setItem('pending_registration_email', payload.email);
    return { pendingBrand: data.pendingBrand, message: data.message };
  }

  /**
   * Complete email-verified commercial brand signup and store session tokens.
   */
  static async completeBrandRegistration(accessToken: string, refreshToken?: string): Promise<{ user: User; token: string; refreshToken?: string; brand: { id: string; name: string }; credits: { availableCredits: number; reservedCredits: number } }> {
    const response = await fetch(`${API_BASE_URL}/auth/complete-brand-registration`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ accessToken, refreshToken }),
    });

    const result = await response.json();
    if (!response.ok || !result.success) {
      throw new Error(result.error || 'Kayıt tamamlanamadı');
    }

    localStorage.setItem('token', result.token);
    if (result.refreshToken) {
      localStorage.setItem('refreshToken', result.refreshToken);
    }
    localStorage.removeItem('pending_registration_email');

    return {
      user: result.user,
      token: result.token,
      refreshToken: result.refreshToken,
      brand: result.brand,
      credits: result.credits,
    };
  }

  /**
   * Send magic link for registration
   */
  static async sendMagicLink(email: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/auth/send-magic-link`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.error || 'Magic link gönderilemedi');
    }
  }

  /**
   * Complete registration with brand info
   */
  static async completeRegistration(data: {
    email: string;
    password: string;
    name: string;
    brandName: string;
    websiteUrl?: string;
    xmlFeedUrl?: string;
    accessToken?: string;
  }): Promise<{ user: User; token: string; refreshToken: string; brand: { id: string; name: string } }> {
    const response = await fetch(`${API_BASE_URL}/auth/complete-registration`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      throw new Error(result.error || 'Kayıt tamamlanamadı');
    }

    if (result.requireLogin) {
      throw new Error('REQUIRE_LOGIN');
    }

    // Store tokens
    localStorage.setItem('token', result.token);
    localStorage.setItem('refreshToken', result.refreshToken);

    return {
      user: result.user,
      token: result.token,
      refreshToken: result.refreshToken,
      brand: result.brand,
    };
  }

  /**
   * Subscribe to auth state changes
   * Note: Since we're using backend API, this is simulated
   * We check localStorage for token changes
   */
  static onAuthStateChanged(callback: (user: User | null) => void): () => void {
    let intervalId: NodeJS.Timeout;

    // Check for auth state every 5 seconds
    const checkAuthState = async () => {
      const user = await this.getCurrentUser();
      callback(user);
    };

    // Initial check
    checkAuthState();

    // Poll for changes
    intervalId = setInterval(checkAuthState, 5000);

    // Return cleanup function
    return () => {
      clearInterval(intervalId);
    };
  }
}
